import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
    type CustomList,
    type CustomListDetail,
    type CustomListItem,
    fetchCustomListDetail,
} from 'entities/CustomList';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

export type AddListConfirmMode = 'add' | 'remove';

export const useAddListToOrderConfirm = (
    listId: string | undefined,
    detail: CustomListDetail | null | undefined,
) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [listItemsCache, setListItemsCache] = useState<
        Record<string, CustomListItem[]>
    >({});
    const [confirmList, setConfirmList] = useState<{
        list: CustomList;
        items: CustomListItem[];
    } | null>(null);
    const [confirmMode, setConfirmMode] = useState<AddListConfirmMode>('add');

    useEffect(() => {
        if (detail?.id && detail.id === listId) {
            setListItemsCache((prev) => ({
                ...prev,
                [detail.id]: detail.items || [],
            }));
        }
    }, [detail, listId]);

    const openConfirmForList = useCallback(
        async (
            list: CustomList,
            items?: CustomListItem[],
            mode: AddListConfirmMode = 'add',
        ) => {
            if (list.itemCount === 0) {
                return;
            }

            let nextItems = items || listItemsCache[list.id];
            if (!nextItems) {
                const result = await dispatch(
                    fetchCustomListDetail({
                        listId: list.id,
                        updateDetail: list.id === listId,
                    }),
                );
                if (fetchCustomListDetail.fulfilled.match(result)) {
                    const fetchedItems = result.payload.items || [];
                    nextItems = fetchedItems;
                    setListItemsCache((prev) => ({
                        ...prev,
                        [list.id]: fetchedItems,
                    }));
                } else {
                    const payloadMessage =
                        typeof result.payload === 'string'
                            ? result.payload
                            : '';

                    toast.error(
                        payloadMessage ||
                            t('customLists.failedToLoadListItems'),
                    );
                    return;
                }
            }

            if (!nextItems?.length) {
                return;
            }

            setConfirmMode(mode);
            setConfirmList({ list, items: nextItems });
        },
        [dispatch, listId, listItemsCache, t],
    );

    const closeConfirm = useCallback(() => {
        setConfirmList(null);
    }, []);

    return {
        openConfirmForList,
        confirmList,
        confirmMode,
        closeConfirm,
    };
};
